import { AlertColor, Box } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useReactToPrint } from "react-to-print";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
// import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined"; // only used by the commented-out Estimated Fund Details card below
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import PopUp from "src/components/common/PopUp";
import Snackbar from "src/components/common/Snackbar";
import { useAppSelector } from "src/redux/store";
import { getStyles } from "src/styles/theme";
import {
  fundApplicationQuery,
  uploadPaymentReciept,
  verifyFundApplication,
} from "src/utils/api/dashboard";
import defaultStyles, { StylesClasses } from "../styles";
import { dateAndTimeStamp } from "src/utils/helper";
import {
  getFundApplicationById,
  updateFundRequestStatus,
} from "src/utils/api/createProjectFlow";
import { Button } from "src/components/common/FormComponents/Buttons";
import { Input } from "src/components/common/FormComponents/Input";
import FileUpload from "src/components/common/FormComponents/FileUplaod";
import errorMessage from "src/constants/errorMessage";
import {
  ActionFooter,
  ActionNote,
  ActionPanel,
  FUND_SECTIONS,
  FundDetailSkeleton,
  FundLoadError,
  PageProgressBar,
  readApiError,
  useScrollToSection,
  AmountRow,
  ConfirmActionDialog,
  queryButtonStyles,
  FormGrid,
  PendingAction,
  ApplicationTimeline,
  DetailField,
  DetailFieldGrid,
  DetailLayout,
  DocumentTile,
  DownloadUcFormatButton,
  hasPreviousUcDetails,
  hasFundBeenReleased,
  hasReachedUcStage,
  PreviousUcSummary,
  previousUcFromCurrentFa,
  SectionCard,
  StatusHistoryTrail,
  StatusPill,
  SubSection,
  SubSectionRow,
  UcSubmitPayload,
  UtilizationCertificatePanel,
  fundUi,
  installmentLabel,
  money,
  shortDate,
} from "./FundApplicationUI";

const FundApplicationDetails = () => {
  const styles = getStyles<StylesClasses>(defaultStyles);

  const [searchParams] = useSearchParams();

  const componentRef = useRef();

  const FundApplicationId = searchParams.get("paymentId");

  // Set when the user arrived by clicking an Action in the fund application
  // list — the panel that action belongs to is scrolled to and ringed.
  const targetSection = searchParams.get("section");

  const user = useAppSelector((state) => state.root.user);

  const { userType } = user.data;

  const navigate = useNavigate();

  // Every fund action is one-way, so each one is confirmed before it runs.
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );

  // Every action on this page ends in a toast — the outcome used to be left to
  // be inferred from the page quietly re-rendering.
  const [feedback, setFeedback] = useState<{
    message: string;
    severity: AlertColor;
  } | null>(null);

  // Read straight off the query's own `data` rather than copying it into a
  // separate `useState` in `onSuccess` — that copy landed a render behind
  // `isLoading` flipping to false, so the page briefly showed its shell with
  // empty fields before the real data arrived. Deriving it here keeps
  // `projectData` and `isLoading` in the same render.
  const {
    data: fundApplicationResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["userDetails", FundApplicationId],
    () => getFundApplicationById(FundApplicationId),
    {
      onError: (error: AxiosError) => {
        setFeedback({
          message: readApiError(
            error,
            "Could not load this fund application. Please try again."
          ),
          severity: "error",
        });
      },
      enabled: !!FundApplicationId,
      refetchOnMount: true,
    }
  );

  const projectData = fundApplicationResponse?.data;

  const { mutate, isLoading: isForwarding } = useMutation(
    "verifyFundApplication",
    ({
      FundApplicationId,
      data,
    }: {
      FundApplicationId: string;
      data: {
        status: string;
        remark: string;
        paymentApplicationDocId?: string;
      };
    }) =>
      verifyFundApplication({
        FundApplicationId,
        data,
      }),
    {
      onSuccess: () => {
        setPendingAction(null);
        navigate("/fund-application?currentTab=CREATED");
      },
      onError: (error: AxiosError) => {
        setPendingAction(null);
        setFeedback({
          message: readApiError(
            error,
            "Could not forward this application. Please try again."
          ),
          severity: "error",
        });
      },
    }
  );

  const { mutate: uploadReceipt, isLoading: isUploadingReceipt } = useMutation(
    "uploadReceipt",
    ({
      FundApplicationId,
      data,
    }: {
      FundApplicationId: string;
      data: {
        status: string;
        paymentAcknowledgeDocId?: string;
        remark?: string;
      };
    }) =>
      uploadPaymentReciept({
        FundApplicationId,
        data,
      }),
    {
      onSuccess: (_response, variables) => {
        // A receipt uploaded at the release acknowledges the fund and hands the
        // application on, so the SNO goes back to the list. One filled in later
        // only attaches the document — the application stays where it was, so
        // stay on the page and let it re-render with the receipt recorded.
        if (variables?.data?.status === "PAYMENT_ACKNOWLEDGED") {
          navigate("/fund-application?currentTab=CREATED");
          return;
        }

        refetch();
      },
      onError: (error: AxiosError) => {
        setPendingAction(null);
        setFeedback({
          message: readApiError(
            error,
            "Could not upload the payment receipt. Please try again."
          ),
          severity: "error",
        });
      },
    }
  );

  // `onSuccess` returns the refetch so react-query holds the mutation in its
  // loading state until the fresh data has landed — otherwise the spinner
  // stops while the page is still showing the pre-action content.
  const { mutate: mutateQueryData, isLoading: isRaisingQuery } = useMutation(
    "query",
    ({ id, query }: { id: string; query: string }) =>
      fundApplicationQuery({
        id,
        query,
      }),
    {
      onSuccess: async () => {
        await refetch();
        setValue("query", "");
        setPendingAction(null);
        setFeedback({
          message: "Your query has been recorded against this application.",
          severity: "success",
        });
      },
      onError: (error: AxiosError) => {
        setPendingAction(null);
        setFeedback({
          message: readApiError(
            error,
            "Could not raise the query. Please try again."
          ),
          severity: "error",
        });
      },
    }
  );

  // Set when the backend rejects a UC submission — most notably because an
  // earlier FA's own UC is not yet approved (see project-service's
  // PREVIOUS_FUND_APPLICATION_UC_NOT_APPROVED check). The panel already
  // blocks this case proactively; this is the fallback for stale client
  // state (e.g. another tab approved/queried an earlier FA in the meantime).
  const [ucSubmitError, setUcSubmitError] = useState<string | null>(null);

  // The SNO uploads the UC for the admin to verify, so this hands the
  // application on rather than accepting it — same status this portal's UC
  // dialog has always patched to.
  const { mutate: submitUc, isLoading: isSubmittingUc } = useMutation(
    (payload: UcSubmitPayload) =>
      updateFundRequestStatus(FundApplicationId, {
        status: "UC_UPLOADED",
        ...payload,
      }),
    {
      onSuccess: async () => {
        await refetch();
        setPendingAction(null);
        setFeedback({
          message:
            "Utilization Certificate submitted. It is now with the admin for verification.",
          severity: "success",
        });
      },
      onError: async (error: any) => {
        const pendingYears: number[] | undefined =
          error?.response?.data?.errors?.pendingYears;
        setPendingAction(null);
        setUcSubmitError(
          pendingYears?.length
            ? `Upload the Utilization Certificate for FA${pendingYears.join(
                ", FA"
              )} and get it approved by the admin before uploading the UC for this Fund Application.`
            : error?.response?.data?.message ||
                "Failed to submit the Utilization Certificate. Please try again."
        );
        await refetch();
      },
    }
  );

  const {
    control,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      remarks: "",
      receiptRemark: "",
      paymentApplicationDocId: [
        {
          file: "",
          key: "",
        },
      ],
    },
  });

  useEffect(() => {
    if (!!projectData) {
      reset({
        remarks: projectData?.fundDetails?.snoRemark,
        paymentApplicationDocId: projectData?.paymentApplicationDocUrl
          ? [
              {
                file: projectData?.paymentApplicationDocUrl,
                key: projectData?.fundDetails?.paymentApplicationDocId,
              },
            ]
          : [],
      });
    }
  }, [projectData]);

  useScrollToSection(targetSection, !isLoading && !!projectData?.fundDetails);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@media print { body { -webkit-print-color-adjust: exact; } }",
  });

  const docId = getValues("paymentApplicationDocId");

  const paymentAcknowledgeDocId = getValues("paymentAcknowledgeDocId");

  // Only one fund action is ever in flight at a time, so a single flag drives
  // the confirmation dialog's spinner and the action panel overlays.
  const isActionRunning =
    isForwarding || isUploadingReceipt || isRaisingQuery || isSubmittingUc;

  const fundDetails = projectData?.fundDetails;

  const landOwnership = fundDetails?.landOwnership;

  const totalArea = Number(landOwnership?.totalArea);

  const status = fundDetails?.status;

  // Archived applications are served read-only from the archive tables.
  const isArchived = !!projectData?.isArchived;

  // What this one application released - 0 until the admin approves it. The
  // payment-receipt panel below talks about this FA alone, so it keeps using
  // this figure rather than the running total.
  const releasedAmount = Number(fundDetails?.totalAmountReleased ?? 0);

  // "Total Amount Released By Admin" is a running total for the block, not for
  // this application alone: FA1 + FA2 + ... up to and including this FA. The
  // backend already computes it (`totalPaymentReleasedTillDate`, the same
  // figure the Fund Application list's Total Balance column is built from), so
  // it is read from there rather than recomputed. Legacy rows with no FA year
  // fall outside that running total, so this FA's own release is the floor.
  const cumulativeReleasedAmount = Math.max(
    Number(projectData?.totalPaymentReleasedTillDate ?? 0),
    releasedAmount
  );

  // Balance is Eco-Restoration Amount minus everything released so far, so it
  // is derived here rather than read from the stored `amountLeft`, which only
  // ever netted off this FA's own release.
  const balanceAmount =
    Number(landOwnership?.demandNote ?? 0) - cumulativeReleasedAmount;

  // The UC section: editable from the moment the fund is released — the SNO
  // may upload the UC alongside the payment receipt rather than after it — and
  // stays editable once the receipt is in or the UC came back rejected.
  // Read-only once it has been uploaded or accepted.
  const ucPanelMode: "upload" | "view" | null = isArchived
    ? status === "UC_UPLOADED" || status === "UC_APPROVED"
      ? "view"
      : null
    : (status === "APPROVED" ||
        status === "PAYMENT_ACKNOWLEDGED" ||
        status === "UC_REJECTED") &&
      userType === "INDIVIDUAL"
    ? "upload"
    : status === "UC_UPLOADED" || status === "UC_APPROVED"
    ? "view"
    : null;

  // The payment receipt belongs to the fund release, not to one status: an
  // application can be carried on to the UC stage with no receipt ever
  // recorded. So the upload stays open to the SNO from the release onwards for
  // as long as no receipt exists, and uploading one late leaves the status
  // exactly where it is rather than walking the application backwards.
  const hasPaymentReceipt =
    !!projectData?.paymentAcknowledgementDocUrl ||
    !!fundDetails?.paymentAcknowledgeDocId;

  const canUploadPaymentReceipt =
    !isArchived &&
    userType === "INDIVIDUAL" &&
    !hasPaymentReceipt &&
    hasFundBeenReleased(status);

  // The receipt records the released fund reaching the SNO; the UC certifies
  // how that fund was then spent. Filing them the other way round certifies a
  // spend against money with no receipt on record, so the UC waits its turn.
  // An application whose UC is already in is past this point and is left
  // alone - it only still needs the receipt, which stays open above.
  const mustUploadReceiptFirst = ucPanelMode === "upload" && !hasPaymentReceipt;

  // Only the receipt filed at the release itself acknowledges the fund and
  // hands the application on; a late one is recorded against the status the
  // application already holds.
  const receiptSubmitStatus =
    status === "APPROVED" ? "PAYMENT_ACKNOWLEDGED" : status;

  // The previous year's UC is context for raising this FA and releasing its
  // fund. Once the SNO has uploaded the payment receipt, this FA's own UC is
  // the subject and the prior year's drops off the page.
  const previousUc = previousUcFromCurrentFa(projectData);

  const showPreviousUc =
    hasPreviousUcDetails(previousUc) && !hasReachedUcStage(status);

  const hasDocuments =
    projectData?.previousUtilizationCertificateDocUrl ||
    projectData?.previousRevalidationLetterDocUrl ||
    projectData?.paymentApplicationDocUrl ||
    projectData?.applicationApproveDocUrl ||
    projectData?.paymentAcknowledgementDocUrl;

  // Shared by the loaded page and its skeleton, so the shell around them is
  // identical and only the body swaps once the data lands.
  const pageSurfaceStyles = {
    children: {
      marginTop: "20px",
      padding: { xs: "16px", md: "22px" },
      paddingBottom: "40px",
      borderRadius: "18px",
      backgroundColor: "#F5FAF8",
      "@media print": { backgroundColor: "#FFFFFF", padding: 0 },
    },
  };

  return (
    <Box>
      {/* A status change lands by refetching in place — this is the only sign
          that the page is still catching up with the action just taken. */}
      <PageProgressBar show={isFetching && !isLoading} />
      {!isLoading && !!fundDetails ? (
        <DashboardWrapper
          title="Fund Application Details"
          customStyles={pageSurfaceStyles}
          buttonText="Print"
          buttonIcon="/assets/svg/export.svg"
          onButtonClick={handlePrint}
          showBackBtn
        >
          <Box ref={componentRef} id="printable-content">
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "16px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "22px",
                    fontWeight: 600,
                    color: fundUi.value,
                  }}
                >
                  Fund Application No : {fundDetails?.id}
                </Box>
                <StatusPill status={status} />
              </Box>
              {!!installmentLabel(fundDetails) && (
                <Box
                  sx={{
                    fontSize: "14px",
                    color: fundUi.label,
                    marginTop: "6px",
                  }}
                >
                  {installmentLabel(fundDetails)}
                </Box>
              )}
            </Box>

            <DetailLayout>
              <SectionCard
                title="Application Timeline"
                icon={<AccessTimeIcon />}
                fullWidth
              >
                <ApplicationTimeline fund={fundDetails} />

                {/* The audit trail on the left, the exact hand-off timestamps
                    in the corner opposite it. */}
                <SubSectionRow>
                  <SubSection title="Status History" divider={false}>
                    <StatusHistoryTrail
                      statusHistory={projectData?.statusHistory}
                    />
                  </SubSection>
                  {(fundDetails?.raisedDate || fundDetails?.releasedDate) && (
                    <SubSection title="Key Dates" align="right" divider={false}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "14px",
                        }}
                      >
                        {fundDetails?.raisedDate && (
                          <DetailField
                            label="Fund Application Raised to Admin"
                            value={dateAndTimeStamp(fundDetails?.raisedDate)}
                          />
                        )}
                        {fundDetails?.releasedDate && (
                          <DetailField
                            label="Fund Released"
                            value={dateAndTimeStamp(fundDetails?.releasedDate)}
                          />
                        )}
                      </Box>
                    </SubSection>
                  )}
                </SubSectionRow>
              </SectionCard>

              <SectionCard
                title="Eco-Restoration Blocks Details"
                icon={<ParkOutlinedIcon />}
                accent
              >
                <DetailFieldGrid columns={2}>
                  <DetailField
                    label="Entity Name"
                    value={landOwnership?.user?.firstName}
                    span
                  />
                  <DetailField
                    label="Land Registration No."
                    value={landOwnership?.projectDetail?.registration_no}
                  />
                  <DetailField
                    label="State"
                    value={
                      landOwnership?.projectDetail?.address?.stateDetail?.name
                    }
                  />
                  <DetailField
                    label="Cost Estimate ID"
                    value={fundDetails?.landOwnershipEpid}
                  />
                  <DetailField
                    label="District"
                    value={
                      landOwnership?.projectDetail?.address?.districtDetail
                        ?.name
                    }
                  />
                  <DetailField
                    label="Eco-Restoration Amount"
                    value={
                      landOwnership?.demandNote
                        ? money(landOwnership?.demandNote)
                        : undefined
                    }
                  />
                  <DetailField
                    label="Division"
                    value={
                      landOwnership?.projectDetail?.landSpecification?.division
                    }
                  />
                  <DetailField
                    label="Total Planned Area (Hectares)"
                    value={landOwnership?.netPlantationArea}
                  />
                  <DetailField
                    label="Land Area Requested (Hectares)"
                    value={
                      Number.isFinite(totalArea)
                        ? totalArea.toFixed(2)
                        : undefined
                    }
                  />
                </DetailFieldGrid>
              </SectionCard>

              {/* Estimated Fund Details - hidden, the SNO no longer fills
              these in on the Raise Fund Application form (see
              RaiseFundApplication), so they now always read 0.
              <SectionCard
                title="Estimated Fund Details for (As Per Cost Estimate Submitted)"
                icon={<InsightsOutlinedIcon />}
              >
                <AmountRow
                  label="Estimated Advance Work Cost"
                  value={money(fundDetails?.estimatedAdvanceWorkCost)}
                />
                <AmountRow
                  label="Estimated Raising Cost"
                  value={money(fundDetails?.estimatedRaisingCost)}
                />
                <AmountRow
                  label="Estimated Maintenance Cost"
                  value={money(fundDetails?.estimatedMaintenanceCost)}
                />
                <AmountRow
                  label="Estimated Monitoring Cost"
                  value={money(fundDetails?.estimatedMonitoringCost)}
                />
                <AmountRow
                  label="Estimated Contingency Cost"
                  value={money(fundDetails?.estimatedContingencyCost)}
                />
                <AmountRow
                  label="Total Estimated Cost"
                  value={money(fundDetails?.totalEstimatedCost)}
                  tone="total"
                />
              </SectionCard> */}

              <SectionCard title="Requested Fund" icon={<CurrencyRupeeIcon />}>
                <AmountRow
                  label="Advance Work Cost"
                  value={money(fundDetails?.advanceWorkCost)}
                />
                <AmountRow
                  label="Raising/Creation Cost"
                  value={money(fundDetails?.raisingCost)}
                />
                <AmountRow
                  label="Maintenance Cost"
                  value={money(fundDetails?.maintenanceCost)}
                />
                <AmountRow
                  label="Monitoring Cost"
                  value={money(fundDetails?.monitoringCost)}
                />
                <AmountRow
                  label="Contingency Cost"
                  value={money(fundDetails?.contingencyCost)}
                />
                <AmountRow
                  label="Total Amount Requested"
                  value={money(fundDetails?.totalCost)}
                  tone="total"
                />
                <AmountRow
                  label="Total Amount Released By Admin"
                  value={money(cumulativeReleasedAmount)}
                  tone="soft"
                />
                <AmountRow
                  label="Balance Amount"
                  value={money(balanceAmount)}
                  tone="strong"
                />
              </SectionCard>

              <SectionCard
                title="Additional Information From IO"
                icon={<InfoOutlinedIcon />}
              >
                <DetailFieldGrid columns={1}>
                  <DetailField
                    label="Additional Information"
                    value={fundDetails?.additionalInfo}
                  />
                </DetailFieldGrid>
              </SectionCard>

              {projectData?.paymentApplicationDocUrl && (
                <SectionCard
                  title="Signed Fund Application from SNO"
                  icon={<DescriptionOutlinedIcon />}
                >
                  <DocumentTile
                    fileUrl={projectData?.paymentApplicationDocUrl}
                    meta={
                      fundDetails?.raisedDate
                        ? `Uploaded on ${shortDate(fundDetails?.raisedDate)}`
                        : undefined
                    }
                  />
                  {fundDetails?.snoRemark && (
                    <Box sx={{ marginTop: "14px" }}>
                      <DetailField
                        label="SNO Remark"
                        value={fundDetails?.snoRemark}
                      />
                    </Box>
                  )}
                </SectionCard>
              )}

              {projectData?.applicationApproveDocUrl && (
                <SectionCard
                  title="Signed Fund Application from Admin"
                  icon={<DescriptionOutlinedIcon />}
                >
                  <DocumentTile
                    fileUrl={projectData?.applicationApproveDocUrl}
                    meta={
                      fundDetails?.releasedDate
                        ? `Uploaded on ${shortDate(fundDetails?.releasedDate)}`
                        : undefined
                    }
                  />
                  {fundDetails?.remark && (
                    <Box sx={{ marginTop: "14px" }}>
                      <DetailField label="Remark" value={fundDetails?.remark} />
                    </Box>
                  )}
                  <Box sx={{ marginTop: "14px" }}>
                    <DetailFieldGrid columns={2}>
                      {/* This release order's own amount. The Requested Fund
                      card above carries the running total across every FA of
                      this block, so this one is labelled for this application
                      to keep the two apart. */}
                      <DetailField
                        label="Amount Released In This Application"
                        value={
                          fundDetails?.totalAmountReleased != null
                            ? money(fundDetails?.totalAmountReleased)
                            : undefined
                        }
                      />
                      <DetailField
                        label="Fund Released Date"
                        value={
                          fundDetails?.releasedDate
                            ? shortDate(fundDetails?.releasedDate)
                            : undefined
                        }
                      />
                    </DetailFieldGrid>
                  </Box>
                </SectionCard>
              )}

              {projectData?.paymentAcknowledgementDocUrl && (
                <SectionCard
                  title="Transaction Receipt"
                  icon={<ReceiptLongOutlinedIcon />}
                >
                  <DocumentTile
                    fileUrl={projectData?.paymentAcknowledgementDocUrl}
                    meta={
                      fundDetails?.paymentAcknowledgementDate
                        ? `Uploaded on ${shortDate(
                            fundDetails?.paymentAcknowledgementDate
                          )}`
                        : undefined
                    }
                  />
                </SectionCard>
              )}

              {showPreviousUc && (
                <SectionCard
                  title="Previous UC"
                  icon={<DescriptionOutlinedIcon />}
                  fullWidth
                >
                  <PreviousUcSummary uc={previousUc} />
                </SectionCard>
              )}

              {!hasDocuments && (
                <SectionCard
                  title="Documents"
                  icon={<DescriptionOutlinedIcon />}
                >
                  <Box sx={{ fontSize: "13px", color: fundUi.label }}>
                    No documents have been uploaded against this application
                    yet.
                  </Box>
                </SectionCard>
              )}

              {fundDetails?.queries?.length > 0 && (
                <SectionCard
                  title="Fund Transfer Query"
                  icon={<ForumOutlinedIcon />}
                  fullWidth
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: "12px",
                    }}
                  >
                    {fundDetails?.queries.map((item) => (
                      <Box
                        key={item?.id ?? item?.created_at}
                        sx={{
                          padding: "12px 14px",
                          borderRadius: "12px",
                          border: `1px solid ${fundUi.border}`,
                          backgroundColor: "#FBFDFC",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            fontSize: "12px",
                            color: fundUi.label,
                            marginBottom: "6px",
                          }}
                        >
                          <Box sx={{ fontWeight: 600 }}>
                            {item?.user?.firstName}
                          </Box>
                          <Box>
                            {dateAndTimeStamp(item?.created_at, "Asia/Kolkata")}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            fontSize: "13px",
                            color: fundUi.value,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {item?.query}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </SectionCard>
              )}
            </DetailLayout>
          </Box>

          {ucPanelMode && (
            <ActionPanel
              id={FUND_SECTIONS.uc}
              highlighted={targetSection === FUND_SECTIONS.uc}
              busy={isSubmittingUc}
              blocked={
                mustUploadReceiptFirst
                  ? {
                      title: "Upload the payment receipt first",
                      message:
                        "No payment receipt has been recorded against the fund released for this application yet. Upload it in G.A.R. 6 format above, then come back here to upload utilization certificate.",
                    }
                  : undefined
              }
              title={
                ucPanelMode === "view"
                  ? "Utilization Certificate"
                  : "Upload Utilization Certificate"
              }
              icon={<DescriptionOutlinedIcon />}
              description={
                ucPanelMode === "view"
                  ? "The utilization certificate recorded against this fund application."
                  : "Record the audited utilization against the fund already released, and attach the certificate for the admin to verify."
              }
              headerRight={
                ucPanelMode === "upload" ? (
                  <DownloadUcFormatButton />
                ) : undefined
              }
            >
              <UtilizationCertificatePanel
                data={projectData}
                ecoRestorationCost={landOwnership?.demandNote}
                mode={ucPanelMode}
                isSubmitting={isSubmittingUc}
                blockedReason={
                  mustUploadReceiptFirst
                    ? "Upload the payment receipt for this application before certifying how the fund was utilized."
                    : undefined
                }
                onSubmit={(payload) =>
                  setPendingAction({
                    title: "Submit this utilization certificate?",
                    message: `${money(
                      payload.utilizedAmount
                    )} will be recorded as utilized against ${
                      fundDetails?.id
                    }, and the certificate sent to the admin for verification.`,
                    confirmText: "Submit UC",
                    onConfirm: () => submitUc(payload),
                  })
                }
              />
            </ActionPanel>
          )}

          {(status === "CREATED" || status === "UNDER_QUERY") &&
            userType === "INDIVIDUAL" &&
            !isArchived && (
              <ActionPanel
                id={FUND_SECTIONS.forward}
                highlighted={targetSection === FUND_SECTIONS.forward}
                title="Forward to Admin"
                busy={isForwarding || isRaisingQuery}
                icon={<DescriptionOutlinedIcon />}
                description="Attach the signed fund application and add your remark to send this application to the admin, or raise a query instead."
              >
                <FormGrid>
                  <FileUpload
                    key={watch("paymentApplicationDocId")?.[0]?.key}
                    name="paymentApplicationDocId"
                    label="Upload Signed Fund Application"
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                    setError={setError}
                    clearErrors={clearErrors}
                    maxLimit={1}
                    required={true}
                    trigger={trigger}
                    customStyles={{
                      wrapper: defaultStyles.borderStyle,
                      fileType: defaultStyles.fileType,
                      label: {
                        whiteSpace: "wrap",
                      },
                    }}
                    rules={{
                      validate: {
                        isLength: (value) => {
                          return value && value["length"] >= 1;
                        },
                      },
                    }}
                    dragDropHeading=""
                  />
                  <Input
                    name="remarks"
                    label="SNO Remarks"
                    placeholder="Add your remark for the admin"
                    control={control}
                    errors={errors}
                    multiline
                    required={true}
                    rules={{ required: errorMessage.required }}
                    customStyles={{
                      input: {
                        height: "auto",
                        fontFamily: "Poppins",
                      },
                    }}
                  />
                  <Input
                    name="query"
                    label="Query"
                    placeholder="Ask a question instead of forwarding"
                    control={control}
                    errors={errors}
                    multiline
                    customStyles={{
                      input: {
                        height: "auto",
                        fontFamily: "Poppins",
                      },
                    }}
                  />
                </FormGrid>
                {/* SNO/Nodal Officer reject right removed */}
                <ActionFooter
                  leftAction={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "6px",
                      }}
                    >
                      <Button
                        text="Raise Query"
                        variant="outlined"
                        onClick={() =>
                          setPendingAction({
                            title: "Send this query?",
                            message:
                              "Your query is recorded against this application and shared with the other party. The application is not forwarded.",
                            confirmText: "Raise Query",
                            onConfirm: () =>
                              mutateQueryData({
                                id: FundApplicationId,
                                query: watch("query"),
                              }),
                          })
                        }
                        disabled={!watch("query")}
                        customStyles={queryButtonStyles}
                      />
                      {!watch("query") && (
                        <ActionNote>
                          Fill the Query field above to raise a query.
                        </ActionNote>
                      )}
                    </Box>
                  }
                >
                  <Button
                    text="Forward To Admin"
                    type="submit"
                    disabled={!isValid}
                    onClick={() =>
                      setPendingAction({
                        title: "Forward this application to the admin?",
                        message: `${fundDetails?.id} moves to the admin for fund release along with the signed fund application and your remark. You will not be able to edit it after this.`,
                        confirmText: "Forward To Admin",
                        onConfirm: () =>
                          mutate({
                            FundApplicationId,
                            data: {
                              status: "RAISED",
                              remark: watch("remarks"),
                              paymentApplicationDocId: docId[0]?.key,
                            },
                          }),
                      })
                    }
                    customStyles={{
                      root: {
                        fontSize: "16px",
                        textWrap: "noWrap",
                      },
                    }}
                  />
                </ActionFooter>
              </ActionPanel>
            )}

          {canUploadPaymentReceipt && (
            <ActionPanel
              id={FUND_SECTIONS.receipt}
              highlighted={targetSection === FUND_SECTIONS.receipt}
              title="Upload Payment Receipt"
              busy={isUploadingReceipt}
              icon={<ReceiptLongOutlinedIcon />}
              description={
                status === "APPROVED"
                  ? `${money(
                      releasedAmount
                    )} has been released against this application. Upload the payment receipt in G.A.R. 6 format to acknowledge it.`
                  : `No payment receipt has been recorded against the ${money(
                      releasedAmount
                    )} released for this application. Upload it in G.A.R. 6 format — the application stays at its current stage.`
              }
            >
              <FormGrid>
                <FileUpload
                  name="paymentAcknowledgeDocId"
                  label="Upload Paymnet Receipt (As Per G.A.R. 6 Format)"
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                  setError={setError}
                  clearErrors={clearErrors}
                  maxLimit={1}
                  required={true}
                  trigger={trigger}
                  customStyles={{
                    wrapper: defaultStyles.borderStyle,
                    fileType: defaultStyles.fileType,
                    label: {
                      whiteSpace: "wrap",
                    },
                  }}
                  rules={{
                    validate: {
                      isLength: (value) => {
                        return value && value["length"] >= 1;
                      },
                    },
                  }}
                  dragDropHeading=""
                />
                <Input
                  name="receiptRemark"
                  label="Your Remark / Query (optional)"
                  placeholder="Add a remark for the admin, or raise a query about this payment."
                  control={control}
                  errors={errors}
                  multiline
                  customStyles={{
                    input: {
                      height: "auto",
                      fontFamily: "Poppins",
                    },
                  }}
                />
              </FormGrid>
              <ActionFooter>
                <Button
                  text="Upload Paymnet Receipt"
                  disabled={!isValid}
                  onClick={() =>
                    setPendingAction({
                      title: "Submit this payment receipt?",
                      message:
                        status === "APPROVED"
                          ? "The uploaded receipt is recorded against this application and the fund is acknowledged as received."
                          : "The uploaded receipt is recorded against this application. Its current status is left unchanged.",
                      confirmText: "Upload Receipt",
                      // Read at confirm time so a remark typed after the
                      // dialog was opened still travels with the receipt.
                      onConfirm: () => {
                        const remark = getValues("receiptRemark")?.trim();

                        uploadReceipt({
                          FundApplicationId,
                          data: {
                            status: receiptSubmitStatus,
                            paymentAcknowledgeDocId:
                              paymentAcknowledgeDocId[0]?.key,
                            ...(remark ? { remark } : {}),
                          },
                        });
                      },
                    })
                  }
                  customStyles={{
                    root: {
                      fontSize: "16px",
                      textWrap: "noWrap",
                    },
                  }}
                />
              </ActionFooter>
            </ActionPanel>
          )}

          <ConfirmActionDialog
            action={pendingAction}
            onClose={() => setPendingAction(null)}
            isProcessing={isActionRunning}
          />

          <PopUp
            open={!!ucSubmitError}
            handleClose={() => setUcSubmitError(null)}
            heading=""
            actionLeft={{ hidden: true }}
            actionRight={{
              label: "Okay",
              onClick: () => setUcSubmitError(null),
            }}
          >
            <Box sx={{ maxWidth: "420px" }}>
              <Box
                sx={{
                  fontSize: "17px",
                  fontWeight: 600,
                  color: fundUi.value,
                  marginBottom: "10px",
                }}
              >
                Utilization Certificate not submitted
              </Box>
              <Box
                sx={{ fontSize: "14px", color: fundUi.label, lineHeight: 1.6 }}
              >
                {ucSubmitError}
              </Box>
            </Box>
          </PopUp>
        </DashboardWrapper>
      ) : isLoading ? (
        // Same shell as the loaded page, so the header and surface are already
        // in place and only the cards fill in.
        <DashboardWrapper
          title="Fund Application Details"
          customStyles={pageSurfaceStyles}
          showBackBtn
        >
          <FundDetailSkeleton />
        </DashboardWrapper>
      ) : (
        <Box {...styles("loading")}>
          {/* Once the fetch has settled with nothing to show, this stops on a
              message with a way out instead of spinning indefinitely. */}
          <FundLoadError onRetry={() => refetch()} />
        </Box>
      )}

      <Snackbar
        open={!!feedback}
        message={feedback?.message ?? ""}
        severity={feedback?.severity ?? "success"}
        resetSnackbar={() => setFeedback(null)}
        // Errors now carry the failing field name, so they need longer than a
        // one-line success confirmation to actually be read.
        duration={feedback?.severity === "error" ? 8000 : 4000}
      />
    </Box>
  );
};

export default FundApplicationDetails;
